import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Network,
  Globe,
  Server,
  Radio,
  Plus,
  RefreshCw,
  Loader2,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import { clsx } from "clsx";
import {
  getPrefixes,
  getAddresses,
  getDNSZones,
  getDHCPLeases,
  allocateAddress,
  type AllocateAddressPayload,
} from "@/api/endpoints/ipam";
import {
  runIpamBackfill,
  type BackfillSummary,
} from "@/api/endpoints/adminIpam";
import { useActiveTenant } from "@/components/tenant/TenantSelector";
import { LoadingSpinner } from "@/components/common/LoadingSpinner";

type IPAMTab = "prefixes" | "addresses" | "dns" | "dhcp";

export default function IPAM() {
  const [activeTab, setActiveTab] = useState<IPAMTab>("prefixes");
  const [tenantId] = useActiveTenant();
  const queryClient = useQueryClient();

  const { data: prefixes, isLoading: prefixesLoading } = useQuery({
    queryKey: ["ipam-prefixes", tenantId],
    queryFn: () => getPrefixes(tenantId ?? undefined),
    enabled: !!tenantId,
  });

  const { data: addresses, isLoading: addressesLoading } = useQuery({
    queryKey: ["ipam-addresses", tenantId],
    queryFn: () => getAddresses(tenantId ?? undefined),
    enabled: !!tenantId,
  });

  const { data: dnsZones, isLoading: dnsLoading } = useQuery({
    queryKey: ["ipam-dns", tenantId],
    queryFn: () => getDNSZones(tenantId ?? undefined),
    enabled: !!tenantId,
  });

  const { data: dhcpLeases, isLoading: dhcpLoading } = useQuery({
    queryKey: ["ipam-dhcp"],
    queryFn: getDHCPLeases,
  });

  const allocate = useMutation({
    mutationFn: (payload: AllocateAddressPayload) => allocateAddress(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ipam-addresses"] });
      queryClient.invalidateQueries({ queryKey: ["ipam-prefixes"] });
    },
  });

  const tabs = [
    { id: "prefixes" as const, label: "Prefixes", icon: Network },
    { id: "addresses" as const, label: "IP Addresses", icon: Globe },
    { id: "dns" as const, label: "DNS Zones", icon: Server },
    { id: "dhcp" as const, label: "DHCP Leases", icon: Radio },
  ];

  const backfillMut = useMutation({
    mutationFn: (dryRun: boolean) => runIpamBackfill(dryRun),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ipam-addresses"] });
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-100">IPAM</h1>
          <p className="mt-1 text-sm text-gray-400">
            IP Address Management via NetBox integration
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => backfillMut.mutate(true)}
            disabled={backfillMut.isPending}
            className="flex items-center gap-2 rounded border border-gray-700 bg-gray-900 px-3 py-2 text-sm text-gray-200 hover:bg-gray-800 disabled:opacity-50"
            title="Dry-run: preview sans ecrire sur NetBox"
          >
            <RefreshCw className={backfillMut.isPending ? "h-4 w-4 animate-spin" : "h-4 w-4"} />
            Backfill (dry-run)
          </button>
          <button
            onClick={() => backfillMut.mutate(false)}
            disabled={backfillMut.isPending}
            className="flex items-center gap-2 rounded bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50"
            title="Synchronise les IPs des VMs locales vers NetBox (rate limit 2/5min)"
          >
            {backfillMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Sync VMs → NetBox
          </button>
        </div>
      </div>

      {backfillMut.isSuccess && backfillMut.data && (
        <BackfillReport summary={backfillMut.data} />
      )}
      {backfillMut.isError && (
        <div className="flex items-center gap-2 rounded border border-red-700/40 bg-red-900/20 p-3 text-sm text-red-300">
          <AlertTriangle className="h-4 w-4" /> {(backfillMut.error as Error).message}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex gap-1 rounded-lg border border-gray-800 bg-gray-900/50 p-1">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={clsx(
              "flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors",
              activeTab === tab.id
                ? "bg-gray-800 text-gray-100"
                : "text-gray-400 hover:text-gray-200"
            )}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Prefixes */}
      {activeTab === "prefixes" && (
        <div className="rounded-xl border border-gray-800">
          {prefixesLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800 bg-gray-900/50">
                  <th className="px-4 py-3 text-left font-medium text-gray-400">
                    Prefix
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-gray-400">
                    VRF
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-gray-400">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-gray-400">
                    Utilization
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-gray-400">
                    Description
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {(prefixes ?? []).map((prefix) => (
                  <tr key={prefix.id} className="hover:bg-gray-900/30">
                    <td className="px-4 py-3 font-mono text-gray-200">
                      {prefix.prefix}
                    </td>
                    <td className="px-4 py-3 text-gray-300">
                      {prefix.vrf ?? "Global"}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={clsx(
                          "rounded-full px-2 py-0.5 text-xs font-medium",
                          prefix.status === "active"
                            ? "bg-green-500/20 text-green-400"
                            : prefix.status === "reserved"
                              ? "bg-yellow-500/20 text-yellow-400"
                              : "bg-gray-500/20 text-gray-400"
                        )}
                      >
                        {prefix.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 w-24 overflow-hidden rounded-full bg-gray-800">
                          <div
                            className="h-full rounded-full bg-blue-500"
                            style={{
                              width: `${prefix.utilization}%`,
                            }}
                          />
                        </div>
                        <span className="text-xs text-gray-400">
                          {prefix.utilization}%
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-400">
                      {prefix.description}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* IP Addresses */}
      {activeTab === "addresses" && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={() => {
                if (prefixes && prefixes.length > 0 && tenantId) {
                  allocate.mutate({
                    prefix_id: prefixes[0].id,
                    tenant_id: tenantId,
                    description: "Allocated from UI",
                  });
                }
              }}
              disabled={allocate.isPending}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
            >
              <Plus className="h-4 w-4" />
              Allocate IP
            </button>
          </div>
          <div className="rounded-xl border border-gray-800">
            {addressesLoading ? (
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner />
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-800 bg-gray-900/50">
                    <th className="px-4 py-3 text-left font-medium text-gray-400">
                      Address
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-400">
                      Hostname
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-400">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-400">
                      Assigned VM
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-400">
                      Description
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {(addresses ?? []).map((addr) => (
                    <tr key={addr.id} className="hover:bg-gray-900/30">
                      <td className="px-4 py-3 font-mono text-gray-200">
                        {addr.address}
                      </td>
                      <td className="px-4 py-3 text-gray-300">
                        {addr.hostname ?? "-"}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={clsx(
                            "rounded-full px-2 py-0.5 text-xs font-medium",
                            addr.status === "active"
                              ? "bg-green-500/20 text-green-400"
                              : addr.status === "dhcp"
                                ? "bg-blue-500/20 text-blue-400"
                                : "bg-gray-500/20 text-gray-400"
                          )}
                        >
                          {addr.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-300">
                        {addr.assigned_vm ?? "-"}
                      </td>
                      <td className="px-4 py-3 text-gray-400">
                        {addr.description}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* DNS Zones */}
      {activeTab === "dns" && (
        <div className="rounded-xl border border-gray-800">
          {dnsLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800 bg-gray-900/50">
                  <th className="px-4 py-3 text-left font-medium text-gray-400">
                    Zone Name
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-gray-400">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-gray-400">
                    Records
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-gray-400">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {(dnsZones ?? []).map((zone) => (
                  <tr key={zone.id} className="hover:bg-gray-900/30">
                    <td className="px-4 py-3 font-mono text-gray-200">
                      {zone.name}
                    </td>
                    <td className="px-4 py-3 capitalize text-gray-300">
                      {zone.type}
                    </td>
                    <td className="px-4 py-3 text-gray-300">
                      {zone.records_count}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={clsx(
                          "rounded-full px-2 py-0.5 text-xs font-medium",
                          zone.status === "active"
                            ? "bg-green-500/20 text-green-400"
                            : "bg-gray-500/20 text-gray-400"
                        )}
                      >
                        {zone.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* DHCP Leases */}
      {activeTab === "dhcp" && (
        <div className="rounded-xl border border-gray-800">
          {dhcpLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800 bg-gray-900/50">
                  <th className="px-4 py-3 text-left font-medium text-gray-400">
                    IP Address
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-gray-400">
                    MAC Address
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-gray-400">
                    Hostname
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-gray-400">
                    Lease End
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-gray-400">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {(dhcpLeases ?? []).map((lease) => (
                  <tr key={lease.id} className="hover:bg-gray-900/30">
                    <td className="px-4 py-3 font-mono text-gray-200">
                      {lease.ip_address}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-300">
                      {lease.mac_address}
                    </td>
                    <td className="px-4 py-3 text-gray-300">
                      {lease.hostname}
                    </td>
                    <td className="px-4 py-3 text-gray-300">
                      {new Date(lease.end_time).toLocaleString()}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={clsx(
                          "rounded-full px-2 py-0.5 text-xs font-medium",
                          lease.status === "active"
                            ? "bg-green-500/20 text-green-400"
                            : "bg-red-500/20 text-red-400"
                        )}
                      >
                        {lease.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

function BackfillReport({ summary }: { summary: BackfillSummary }) {
  const hasFailures = summary.failed > 0 || summary.partial > 0;
  return (
    <div className="rounded border border-gray-800 bg-gray-900/50 p-3 text-sm">
      <div className="mb-2 flex items-center gap-2 font-medium text-gray-200">
        {hasFailures ? (
          <AlertTriangle className="h-4 w-4 text-amber-400" />
        ) : (
          <CheckCircle2 className="h-4 w-4 text-emerald-400" />
        )}
        Backfill {summary.dry_run ? "(dry-run)" : ""} —{" "}
        {summary.results.length} VMs scannes
      </div>
      <div className="flex flex-wrap gap-3 text-xs text-gray-400">
        <span>enriched <b className="text-emerald-300">{summary.enriched}</b></span>
        <span>already_ok <b className="text-gray-300">{summary.already_ok}</b></span>
        <span>skipped <b className="text-gray-500">{summary.skipped}</b></span>
        <span>partial <b className="text-amber-300">{summary.partial}</b></span>
        <span>failed <b className="text-red-400">{summary.failed}</b></span>
      </div>
      {hasFailures && (
        <details className="mt-2">
          <summary className="cursor-pointer text-xs text-gray-400">
            Detail des cas non-ok
          </summary>
          <ul className="mt-1 space-y-1 text-xs font-mono">
            {summary.results
              .filter((r) => r.status === "failed" || r.status === "partial")
              .map((r) => (
                <li key={r.vm_id} className="text-gray-300">
                  [{r.status}] vm#{r.vm_id} {r.vm_name}{" "}
                  {r.ip_address ? `(${r.ip_address})` : ""} — {r.detail}
                </li>
              ))}
          </ul>
        </details>
      )}
    </div>
  );
}
